import { useCallback, useMemo } from 'react';
import type { CaptureMode, SDKCaptureResult, CaptureProgress } from '@ethbridge/core';
import { useBridgeContext, useBridgeSelector, type CaptureOptions, type CaptureState } from '../context/BridgeContext';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Capture hook options
 */
export interface UseCaptureOptions {
  /** Default capture mode */
  defaultMode?: CaptureMode;

  /** Default capture options */
  defaultOptions?: CaptureOptions;

  /** Callback on capture success */
  onSuccess?: (result: SDKCaptureResult) => void;

  /** Callback on capture error */
  onError?: (error: Error) => void;

  /** Callback on capture progress */
  onProgress?: (progress: CaptureProgress) => void;
}

/**
 * Return type for useCapture hook
 */
export interface UseCaptureReturn {
  /** Current capture status */
  status: CaptureState['status'];

  /** Whether capture is in progress */
  isCapturing: boolean;

  /** Whether capture succeeded */
  isSuccess: boolean;

  /** Whether capture failed */
  isError: boolean;

  /** Whether idle (no capture in progress) */
  isIdle: boolean;

  /** Capture result data */
  data: SDKCaptureResult | null;

  /** Capture error */
  error: Error | null;

  /** Capture progress */
  progress: CaptureProgress | null;

  /** Start a capture */
  capture: (mode?: CaptureMode, options?: CaptureOptions) => void;

  /** Start a capture (async) */
  captureAsync: (mode?: CaptureMode, options?: CaptureOptions) => Promise<SDKCaptureResult>;

  /** Cancel current capture */
  cancel: () => void;

  /** Reset capture state */
  reset: () => void;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Specialized hook for biometric capture
 *
 * @param options - Hook options
 * @returns Capture state and controls
 *
 * @example
 * ```tsx
 * function FingerprintCapture() {
 *   const {
 *     capture,
 *     isCapturing,
 *     data,
 *     error,
 *     progress,
 *   } = useCapture({
 *     defaultMode: 'fingerprint',
 *     onSuccess: (result) => console.log('Captured:', result),
 *   });
 *
 *   return (
 *     <div>
 *       <button onClick={() => capture()} disabled={isCapturing}>
 *         {isCapturing ? 'Capturing...' : 'Capture'}
 *       </button>
 *       {progress && <p>Progress: {progress.percentage}%</p>}
 *       {error && <p>Error: {error.message}</p>}
 *       {data && <p>Quality: {data.quality}</p>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useCapture(options: UseCaptureOptions = {}): UseCaptureReturn {
  const { defaultMode = 'fingerprint', defaultOptions, onSuccess, onError, onProgress } = options;

  const context = useBridgeContext();

  // Capture state
  const status = useBridgeSelector((state) => state.capture.status);
  const data = useBridgeSelector((state) => state.capture.data);
  const error = useBridgeSelector((state) => state.capture.error);
  const progress = useBridgeSelector((state) => state.capture.progress);

  // Derived state
  const isCapturing = status === 'capturing' || status === 'processing';
  const isSuccess = status === 'success';
  const isError = status === 'error';
  const isIdle = status === 'idle';

  // Capture function
  const capture = useCallback(
    (mode?: CaptureMode, captureOptions?: CaptureOptions) => {
      const finalMode = mode ?? defaultMode;
      const finalOptions = { ...defaultOptions, ...captureOptions };
      context.capture(finalMode, finalOptions);
    },
    [context, defaultMode, defaultOptions]
  );

  // Async capture function
  const captureAsync = useCallback(
    async (mode?: CaptureMode, captureOptions?: CaptureOptions) => {
      const finalMode = mode ?? defaultMode;
      const finalOptions = { ...defaultOptions, ...captureOptions };

      try {
        const result = await context.captureAsync(finalMode, finalOptions);
        onSuccess?.(result);
        return result;
      } catch (err) {
        const captureError = err instanceof Error ? err : new Error(String(err));
        onError?.(captureError);
        throw captureError;
      }
    },
    [context, defaultMode, defaultOptions, onSuccess, onError]
  );

  // Cancel function
  const cancel = useCallback(() => {
    context.cancelCapture();
  }, [context]);

  // Reset function (currently just cancels)
  const reset = useCallback(() => {
    context.cancelCapture();
  }, [context]);

  // Call onProgress callback
  if (progress && onProgress) {
    onProgress(progress);
  }

  return useMemo(
    () => ({
      status,
      isCapturing,
      isSuccess,
      isError,
      isIdle,
      data,
      error,
      progress,
      capture,
      captureAsync,
      cancel,
      reset,
    }),
    [status, isCapturing, isSuccess, isError, isIdle, data, error, progress, capture, captureAsync, cancel, reset]
  );
}
